/**
 * Pure, React-free helpers for the Room domain: role metadata, blank-entity
 * factories, id generation, identity→member seeding, and validation.
 */

import { v4 as uuid } from 'uuid';
import type { Identity } from '../../auth/identityProvider';
import type { Room, RoomMember, RoomRole, RoomSummary } from './types';

/** All room roles with display metadata (used by member editors). */
export const ROOM_ROLES: { value: RoomRole; label: string; hint: string }[] = [
  { value: 'owner', label: 'Owner', hint: 'Full control; created the room' },
  { value: 'admin', label: 'Admin', hint: 'Manage members and settings' },
  { value: 'member', label: 'Member', hint: 'Create tasks and estimates' },
  { value: 'viewer', label: 'Viewer', hint: 'Read-only access' },
];

/** Human-readable label for a role. */
export function roleLabel(role: RoomRole): string {
  return ROOM_ROLES.find((r) => r.value === role)?.label ?? role;
}

/** Deterministic-ish document id for a new room. */
export function roomDocId(): string {
  return `room_${uuid()}`;
}

/** Builds a RoomMember from an identity at a given role. */
export function memberFromIdentity(
  identity: Identity,
  role: RoomRole = 'member',
): RoomMember {
  return { alias: identity.alias, name: identity.name, role };
}

/**
 * Builds a RoomMember from a person-like record (e.g. a team member). Falls
 * back to the alias for the display name when no name is supplied.
 */
export function memberFromPerson(
  person: { alias: string; name?: string },
  role: RoomRole = 'member',
): RoomMember {
  const alias = person.alias.trim();
  return { alias, name: (person.name ?? '').trim() || alias, role };
}

/**
 * True when the given alias is the room's creator or one of its members. Uses
 * the summary's mirrored, lowercased {@link RoomSummary.memberAliases} so no S3
 * hydration is needed. Membership check:
 *   alias(lowercased) === createdBy(lowercased) || memberAliases includes it.
 */
export function isRoomMember(summary: RoomSummary, alias: string): boolean {
  const a = alias.trim().toLowerCase();
  if (!a) return false;
  if ((summary.createdBy ?? '').trim().toLowerCase() === a) return true;
  return summary.memberAliases.includes(a);
}

/**
 * Filters summaries to those the alias is a member of (creator or member).
 * Callers fall back to the full list when this is empty so no one is ever
 * locked out.
 */
export function roomsAccessibleTo(
  summaries: RoomSummary[],
  alias: string,
): RoomSummary[] {
  return summaries.filter((s) => isRoomMember(s, alias));
}

/** Creates a new, empty room shell for the create form. */
export function blankRoom(): Room {
  return {
    id: '',
    name: '',
    description: '',
    createdBy: '',
    members: [],
    defaultTemplateId: undefined,
    createdAt: new Date().toISOString(),
    deleted: false,
  };
}

/**
 * Validates a room for save. Returns a list of human-readable problems; an
 * empty list means the room is valid.
 */
export function validateRoom(room: Room): string[] {
  const problems: string[] = [];
  if (!room.name.trim()) problems.push('Room name is required.');

  const seen = new Set<string>();
  room.members.forEach((m, i) => {
    const n = i + 1;
    if (!m.alias.trim()) problems.push(`Member ${n}: alias is required.`);
    if (m.alias.trim()) {
      const key = m.alias.trim().toLowerCase();
      if (seen.has(key)) {
        problems.push(`Member ${n}: duplicate alias "${m.alias}".`);
      }
      seen.add(key);
    }
  });

  const owners = room.members.filter((m) => m.role === 'owner').length;
  if (room.members.length > 0 && owners === 0) {
    problems.push('At least one member must be an owner.');
  }

  return problems;
}

/** Adds a member if the alias is not already present; returns a new array. */
export function upsertMember(
  members: RoomMember[],
  member: RoomMember,
): RoomMember[] {
  const key = member.alias.trim().toLowerCase();
  const idx = members.findIndex((m) => m.alias.trim().toLowerCase() === key);
  if (idx === -1) return [...members, member];
  const next = members.slice();
  next[idx] = member;
  return next;
}

/** Sorts summaries newest-first by creation time. */
export function sortRoomsByRecency(summaries: RoomSummary[]): RoomSummary[] {
  return summaries
    .slice()
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}
